import { Quote, BadgeCheck, ArrowUpRight, Heart } from "lucide-react";
import { useLang } from "@/i18n";
import { BOOKING_URL } from "@/photos";
import { FadeUp } from "./Reveal";

const LABELS = {
  it: {
    eyebrow: "Recensioni",
    title: "Gli ospiti parlano di noi",
    scoreWord: "Favoloso",
    basedOn: "su Booking.com",
    count: "8 recensioni",
    verified: "Recensione verificata Booking",
    readAll: "Leggi tutte le recensioni",
  },
  en: {
    eyebrow: "Reviews",
    title: "Our guests say it best",
    scoreWord: "Fabulous",
    basedOn: "on Booking.com",
    count: "8 reviews",
    verified: "Verified Booking review",
    readAll: "Read all reviews",
  },
};

const REVIEWS = [
  {
    name: "Omar",
    country: { it: "Italia", en: "Italy" },
    score: "8,8",
    title: "Struttura accogliente, pulizia ottimale!",
    text: "Struttura accogliente, pulizia ottimale! Ottimo rapporto qualità prezzo! Letto confortabile, a/c, ampio giardino. Personale gentile e disponibile. Parcheggio privato davanti l'ingresso.",
  },
  {
    name: "Cesidio",
    country: { it: "Svizzera", en: "Switzerland" },
    score: "8,8",
    title: "Appartamento comodo, molto pulito e ordinato",
    text: "Appartamento comodo, molto pulito e ordinato, arredato con gusto, dotato di tutti i comfort. Zona silenziosa e tranquilla. Non centrale rispetto a Roma ma vicino al mare. Per due persone come noi, soggiorno ideale.",
  },
];

export default function Reviews() {
  const { lang } = useLang();
  const l = LABELS[lang];

  return (
    <section id="recensioni" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="reviews-section">
      <FadeUp>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-700">{l.eyebrow}</p>
        <h2 className="font-display mt-3 max-w-2xl text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
          {l.title}
        </h2>
      </FadeUp>

      <div className="mt-14 grid gap-5 lg:grid-cols-[1fr_1.7fr]">
        <FadeUp delay={0.1}>
          <div className="flex h-full flex-col justify-between gap-10 rounded-3xl bg-gradient-to-br from-teal-700 to-teal-900 p-8 sm:p-10">
            <div>
              <div className="flex items-end gap-3">
                <span className="font-display text-7xl font-bold leading-none text-white sm:text-8xl">8,8</span>
                <Heart className="mb-2 h-7 w-7 fill-pink-400 text-pink-400" />
              </div>
              <p className="font-display mt-3 text-2xl italic text-teal-100">{l.scoreWord}</p>
              <p className="mt-1 text-sm font-medium text-teal-200/80">
                {l.count} · {l.basedOn}
              </p>
            </div>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-read-all-link"
              className="group inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-teal-900 transition-[transform,background-color] duration-300 hover:scale-105 hover:bg-pink-50"
            >
              {l.readAll}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </FadeUp>

        <div className="grid gap-5 sm:grid-cols-2">
          {REVIEWS.map((review, i) => (
            <FadeUp key={review.name} delay={0.18 + i * 0.1}>
              <figure
                className="flex h-full flex-col gap-4 rounded-3xl border border-stone-200/80 bg-white p-7 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-pink-300 hover:shadow-xl hover:shadow-pink-600/10"
                data-testid={`review-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <Quote className="h-8 w-8 fill-pink-100 text-pink-300" />
                  <span className="rounded-xl bg-pink-600 px-3 py-1.5 text-sm font-bold text-white shadow-md shadow-pink-600/25">
                    {review.score}
                  </span>
                </div>
                <blockquote className="flex-1">
                  <p className="font-display text-lg font-bold leading-snug text-stone-900">"{review.title}"</p>
                  <p className="mt-3 text-sm leading-relaxed text-stone-500">{review.text}</p>
                </blockquote>
                <figcaption className="border-t border-stone-100 pt-4">
                  <p className="text-sm font-bold text-stone-900">
                    {review.name} · {review.country[lang]}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-teal-700">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    {l.verified}
                  </p>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
